/**
 * @title isNoEmpty<T>
 * @description 是有效值 (非undefined , null, NaN, '')
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.3.0
 */
function isValid(value) {
    return (value !== undefined && value !== null && value === value && value !== '');
}
/**
 * @title isEmpty
 * @description 是无效值 undefined , null, NaN
 * @param value {unknown} 待值
 * @returns {boolean}
 */
function isEmpty(value) {
    return value === undefined || value === null || value !== value;
}
/**
 * @title isNoEmpty<T>
 * @description 不是是无效值 undefined , null, NaN
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.3.0
 */
function isNoEmpty(value) {
    return value !== undefined && value !== null && value === value;
}
/**
 * @title isNull
 * @param {unknown} value
 * @returns {boolean}
 */
function isNull(value) {
    return value === null;
}
/**
 * @title isNoNull<T>
 * @param {unknown} value
 * @returns {boolean}
 */
function isNoNull(value) {
    return value !== null;
}
/**
 * @title isUndefined
 * @param {unknown} value
 * @returns {boolean}
 */
function isUndefined(value) {
    return value === undefined;
}
/**
 * @title isNoUndefined<T>
 * @param {unknown} value
 * @returns {boolean}
 */
function isNoUndefined(value) {
    return value !== undefined;
}
/**
 * @title isNaN
 * @param {unknown} value
 * @returns {boolean}
 */
const isNaN$1 = Number.isNaN;

/**
 * @title isIterator
 * @description 是iterator
 * @param {unknown} value
 * @returns {boolean}
 */
function isIterator(value) {
    if (typeof value !== 'object' || isEmpty(value))
        return false;
    return /Iterator\]$/.test(Object.prototype.toString.call(value));
}
/**
 * @title isArrayIterator
 * @description 是 Array Iterator
 * @param {unknown} value
 * @returns {boolean}
 */
function isArrayIterator(value) {
    if (typeof value !== 'object' || isEmpty(value))
        return false;
    return Object.prototype.toString.call(value) === '[object Array Iterator]';
}
/**
 * @title isMapIterator
 * @description 是 Map Iterator
 * @param {unknown} value
 * @returns {boolean}
 */
function isMapIterator(value) {
    if (typeof value !== 'object' || isEmpty(value))
        return false;
    return Object.prototype.toString.call(value) === '[object Map Iterator]';
}
/**
 * @title isSetIterator
 * @description 是 Set Iterator
 * @param {unknown} value
 * @returns {boolean}
 */
function isSetIterator(value) {
    if (typeof value !== 'object' || isEmpty(value))
        return false;
    return Object.prototype.toString.call(value) === '[object Set Iterator]';
}

function type(param) {
    try {
        if (isIterator(param))
            return 'Iterator';
        const result = Object.prototype.toString
            .call(param)
            .match(/\[object (\w+)\]/)[1];
        if (result === 'Number' && isNaN(param))
            return 'NaN';
        return result;
    }
    catch (error) {
        return 'Undefined';
    }
}
/**
 * @title types
 * @description 获取类型数组
 * @param {unknown[]} params 待判断的参数列表
 * @param {boolean} [hasRepeat=false] 保留重复类型
 * @return {string[]} 类型名称
 */
function types(params, hasRepeat = false) {
    if (!Array.isArray(params))
        return [];
    const result = params.map(i => type(i)) || [];
    return hasRepeat ? result : [...new Set(result)];
}

const MAX_VALUES_NUMBER = 1.7976931348623157e308;
const MIN_VALUES_NUMBER = -1.7976931348623157e308;
const INFINITY = Infinity; // 无限
/**
 * @description 空值(undefined, null, NaN)
 */
const EMPTY = [undefined, null, NaN];

/**
 * @title isObject
 * @description 是Object
 * @param {any} value
 * @returns {boolean}
 */
function isObject(value) {
    return value !== null && typeof value === 'object' && type(value) === 'Object';
}
/**
 * @title isEmptyObject
 * @description 是空Object
 * @param {any} value
 * @returns {boolean}
 */
function isEmptyObject(value) {
    if (isEmpty(value))
        return false;
    return isObject(value) && Reflect.ownKeys(value).length === 0;
}
/**
 * @title isEffectObject
 * @description 是有效Object(非空)
 * @param {any} value
 * @returns {boolean}
 */
function isEffectObject(value) {
    if (isEmpty(value))
        return false;
    return isObject(value) && Reflect.ownKeys(value).length !== 0;
}

/**
 * @title isString
 * @description 是字符串
 * @param {unknown} value
 * @returns {boolean}
 */
function isString(value) {
    return typeof value === 'string';
}
/**
 * @title isJsonString<T>
 * @description 是json字符串, 若是并返回处理后的对象
 * @param {unknown} val 待判断字符串
 * @returns {boolean}
 */
function isJsonString(val) {
    if (!isString(val))
        return false;
    try {
        return isObject(JSON.parse(val));
    }
    catch (e) {
        return false;
    }
}

/**
 * @title isNumber
 * @description 是数字
 * @param {unknown} value
 * @returns {boolean}
 */
const isNumber = (value) => {
    return typeof value === 'number' && type(value) === 'Number';
};
/**
 * @title isEffectNumber
 * @description 是js的有效区间的数, 非number类型都为false
 * @param {unknown} value
 * @returns {boolean}
 */
function isEffectNumber(value) {
    if (type(value) === 'Number') {
        if (value === INFINITY || value === -INFINITY)
            return false;
        return true;
    }
    return false;
}
/**
 * @title isFloat
 * @description 判断数是浮点型
 * @param {unknown} value 待检测的数据类型
 * @returns {boolean}
 */
function isFloat(value) {
    if (!isNumber(value))
        return false;
    return value % 1 !== 0;
}
/**
 * @title isInteger
 * @description 判断数是整型
 * @param {unknown} value 待检测的数据类型
 * @returns {boolean}
 */
function isInteger(value) {
    if (!isNumber(value))
        return false;
    return value % 1 === 0;
}
/**
 * @title isBigInt
 * @description 是bigInt类型
 * @param {unknown} value 待检测的数据类型
 * @returns {boolean}
 */
const isBigInt = (value) => type(value) === 'BigInt';
/**
 * @title likeNumber
 * @description 是数字/数字字符串
 * @support: Number, NumberString
 * @unsupported: Infinity, Function
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.1.0
 */
function likeNumber(value) {
    if (isNumber(value))
        return true;
    if (isEmpty(value) || value === '')
        return false;
    if (isString(value)) {
        value = value.trim();
        if (value.length && Number.isNaN(Number(value)) === false) {
            return true;
        }
        return false;
    }
    return false;
}

/**
 * @title isArray
 * @description 是数组
 * @param {unknown} list
 * @returns {boolean}
 */
function isArray(list) {
    return Array.isArray(list);
}
// export const isArray = Array.isArray
/**
 * @title isNumberArray
 * @description 是数字数组
 * @param {unknown} list
 * @returns {boolean}
 */
function isNumberArray(list) {
    if (!isArray(list)) {
        return false;
    }
    for (let i = 0; i < list.length; i++) {
        if (isNumber(list[i])) {
            continue;
        }
        return false;
    }
    return true;
}
/**
 * @title isEmptyArray
 * @description 是空数组
 * @param {unknown} list
 * @returns {boolean}
 */
function isEmptyArray(list) {
    if (isEmpty(list))
        return false;
    return isArray(list) && list.length === 0;
}
/**
 * @title isEffectArray
 * @description 是有效数组
 * @param {unknown} list
 * @returns {boolean}
 */
function isEffectArray(list) {
    if (isEmpty(list))
        return false;
    return isArray(list) && list.length > 0;
}

/**
 * @title isFunction
 * @description 是函数
 * @param {unknown} value
 * @param {boolean} [strict=false] 严格模式
 * @returns {boolean}
 */
function isFunction(value, strict = false) {
    if (strict) {
        return typeof value === 'function';
    }
    return type(value) === 'Function';
}
/**
 * @title isAsyncFunction
 * @description 是异步函数
 * @param {unknown} value
 * @returns {boolean}
 */
function isAsyncFunction(value) {
    return type(value) === 'AsyncFunction';
}
/**
 * @title likeFunction
 * @description 是函数
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.4.0
 */
function likeFunction(value) {
    return type(value) === 'Function' || type(value) === 'AsyncFunction';
}
/**
 * @title isEmptyFunction
 * @description 是空方法
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.4.0
 */
function isEmptyFunction(value) {
    if (isEmpty(value))
        return false;
    return isFunction(value) && /\{\}$/.test(value.toString().replaceAll(' ', ''));
}
/**
 * @title isEffectFunction
 * @description 是有效方法(非空方法)
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.4.0
 */
function isEffectFunction(value) {
    if (isEmpty(value))
        return false;
    return isFunction(value) && /\{.+\}$/.test(value.toString().replaceAll(' ', ''));
}

/**
 * @title isDate
 * @description 检查日期是否有效
 * @param {unknown} date 待判断日期
 * @returns {boolean}
 * @version 0.1.0
 */
function isDate(date) {
    return type(date) === 'Date';
}
/**
 * @title likeDate
 * @description 检查日期是否有效, 时间戳也为有效时间(13位/10位)
 * @param {unknown} date
 * @returns {boolean}
 */
function likeDate(date) {
    if (isDate(date))
        return true;
    if ((isString(date) && isNumber(Number(date)))
        || isNumber(date)) {
        if (date.toString().length === 13)
            return true;
        if (date.toString().length === 10)
            return true;
    }
    return false;
}
/**
 * @title isTime
 * @description 检查日期是否有效, 时间戳也为有效时间(13位/10位)
 * @param {unknown} time 待判断日期
 * @returns {boolean}
 * @version 0.1.0
 */
function isTime(time) {
    if (isNumber(time)) {
        if (time.toString().length === 13)
            return true;
        if (time.toString().length === 10)
            return true;
    }
    return time instanceof Date && !isNaN(time.getTime());
}
// 该方法用于检测给出的日期是否有效：
// const isDateValid = (...val) => !Number.isNaN(new Date(...val).valueOf());
// isDateValid("December 17, 1995 03:24:00");  // true

/**
 * @title FileTypeMap
 * @description 文件类型映射
 * @version 0.1.0
 */
const FileTypeMap = new Map([
    ['Image', ['png', 'jpeg', 'jpg', 'png', 'bmp']],
    ['Video', ['mp4', 'webm', 'ogg']],
    ['PDF', ['pdf']],
    ['Word', ['doc', 'docx']],
    ['Excel', ['.xlsx', '.xls', '.csv']]
]);
/**
 * @title isFileExtension
 * @description 是指定字符串结尾
 * @param {string} fileName 文件名
 * @param {string[]} list 文件拓展名数组
 * @returns boolean
 * @version 0.1.0
 */
function isFileExtension(fileName, list = []) {
    if (typeof fileName !== 'string')
        return false;
    if (/\.[a-zA-Z0-9]{1,}/.exec(fileName) === null)
        return false;
    if (list.length === 0)
        return true;
    const name = fileName.toLowerCase();
    return list.some(i => name.endsWith(i) === true);
}
/**
 * @title isFile
 * @param {string} fileName
 * @param {FileType} type
 * @returns {boolean}
 * @version 0.1.0
 */
function isFile(fileName, type) {
    return isFileExtension(fileName, FileTypeMap.get(type));
}
/**
 * @title isImageFile
 * @param {string} fileName
 * @returns {boolean}
 * @version 0.1.0
 */
function isImageFile(fileName) {
    return isFileExtension(fileName, FileTypeMap.get('Image'));
}
/**
 * @title isVideoFile
 * @param {string} fileName
 * @returns {boolean}
 * @version 0.1.0
 */
function isVideoFile(fileName) {
    return isFileExtension(fileName, FileTypeMap.get('Video'));
}
/**
 * @title isPdfFile
 * @param {string} fileName
 * @returns {boolean}
 * @version 0.1.0
 */
function isPdfFile(fileName) {
    return isFileExtension(fileName, FileTypeMap.get('PDF'));
}
/**
 * @title isWordFile
 * @param {string} fileName
 * @returns {boolean}
 * @version 0.1.0
 */
function isWordFile(fileName) {
    return isFileExtension(fileName, FileTypeMap.get('Word'));
}
/**
 * @title isExcelFile
 * @param {string} fileName
 * @returns {boolean}
 * @version 0.1.0
 */
function isExcelFile(fileName) {
    return isFileExtension(fileName, FileTypeMap.get('Excel'));
}
/**
 * @title isClient
 * @description 是否为客户端
 */
const isClient = typeof window !== 'undefined';
/**
 * @title isBoolean
 * @description 是布尔值
 * @param {unknown} val
 * @returns {boolean}
 */
const isBoolean = (val) => typeof val === 'boolean';
/**
 * @title isWindow
 * @description 是否为window端
 * @param {unknown} val
 * @returns {boolean}
 */
const isWindow = (val) => typeof window !== 'undefined' && toString.call(val) === '[object Window]';
/**
 * @title isIOS
 * @returns {boolean}
 */
const isIOS = 
 isClient &&
    window?.navigator?.userAgent &&
    /iP(ad|hone|od)/.test(window.navigator.userAgent);
/**
 * @title hasOwn
 * @param {object} val
 * @param {string} key
 * @returns {boolean}
 */
const hasOwn = (val, key) => Object.prototype.hasOwnProperty.call(val, key);

/**
 * @title isHexColor
 * @description 是颜色字符串或数字
 * @param {string} hex 三位/六位的十六进制的颜色
 * @returns {boolean}
 */
function isHexColor(hex) {
    if (hex.length === 3) {
        hex += hex;
    }
    return (type(hex) === 'String')
        && hex.length === 6 && !isNaN(Number('0x' + hex));
}

/**
 * @title isRegExp
 * @description 判断是否为正则表达式
 * @param {unknown} value
 * @returns {boolean}
 */
function isRegExp(value) {
    return type(value) === 'RegExp';
}

/**
 * @title isSymbol
 * @param {unknown} value
 * @returns {boolean}
 */
function isSymbol(value) {
    return typeof value === 'symbol';
}

/**
 * @title isError
 * @param {unknown} value
 * @returns {boolean}
 */
function isError(value) {
    return type(value) === 'Error' || value instanceof Error;
}

/**
 * @title isSet
 * @param {unknown} value
 * @returns {boolean}
 */
function isSet(value) {
    return type(value) === 'Set';
}
/**
 * @title isEmptySet
 * @param {unknown} value
 * @returns {boolean}
 */
function isEmptySet(value) {
    return isSet(value) && value.size === 0;
}
/**
 * @title isEffectSet
 * @param {unknown} value
 * @returns {boolean}
 */
function isEffectSet(value) {
    return isSet(value) && value.size > 0;
}

/**
 * @title isMap
 * @param {unknown} value
 * @returns {boolean}
 */
function isMap(value) {
    return type(value) === 'Map';
}
/**
 * @title isEmptyMap
 * @description 是空Map
 * @param {unknown} value
 * @returns {boolean}
 */
function isEmptyMap(value) {
    if (isEmpty(value))
        return false;
    return isMap(value) && isUndefined(value.keys().next().value);
}
/**
 * @title isEffectMap
 * @description 是有效Map
 * @param {unknown} value
 * @returns {boolean}
 */
function isEffectMap(value) {
    if (isEmpty(value))
        return false;
    return isMap(value) && !isEmpty(value.keys().next().value);
}

/**
 * @title isPromise
 * @param {unknown} value
 * @returns {boolean}
 */
function isPromise(value) {
    return type(value) === 'Promise';
}

/**
 * @title isInstance
 * @param {unknown} value
 * @param {unknown} InStance
 * @returns {boolean}
 */
function isInstance(value, InStance) {
    if (!value)
        return false;
    if (!value.__proto__)
        return false;
    if (!InStance.prototype)
        return false;
    while (value) {
        if (value.__proto__ === InStance.prototype)
            return true;
        else
            value = value.__proto__;
    }
    return false;
}

function IS(type$1) {
    if (isString(type$1)) {
        type$1 = type$1.replace(/ |\n|\t/gi, '');
        // object string
        if (/^{(.+)}$/.test(type$1)) {
            const typeObject = JSON.parse(type$1.replace(/(\w+)/gi, `"$&"`));
            return (value) => {
                if (!isObject(value))
                    return false;
                if (Object.keys(value).length !== Object.keys(typeObject).length)
                    return false;
                for (const key in typeObject) {
                    if (type(value[key]) !== typeObject[key]
                        && !IS(typeObject[key])(value[key])) {
                        return false;
                    }
                }
                return true;
            };
        }
        // array string
        if (/^\[(.+)\]$/.test(type$1)) {
            const [, newType] = /\[(.+)\]$/.exec(type$1) || [undefined, undefined];
            const newTypes = isString(newType) ? newType.split(',') : [];
            return (value) => {
                if (!isArray(value))
                    return false;
                if (newTypes.length !== value.length)
                    return false;
                for (let i = 0; i < value.length; i++)
                    if (type(value[i]) !== newTypes[i])
                        return false;
                return true;
            };
        }
        // type array
        if (/\[\]$/.test(type$1)) {
            const [, newType] = /(.+)\[\]$/.exec(type$1) || [undefined, undefined];
            return (value) => {
                if (!isArray(value))
                    return false;
                for (let i = 0; i < value.length; i++)
                    if (type(value[i]) !== newType
                        && !IS(newType)(value[i])) {
                        return false;
                    }
                return true;
            };
        }
        // mult type
        if (type$1.indexOf('|') > -1) {
            const newTypes = type$1.split('|');
            return (value) => {
                return newTypes.includes((type(value)));
            };
        }
    }
    if (isObject(type$1)) {
        return (value) => {
            if (!isObject(value))
                return false;
            for (const key in type$1) {
                if (type(value[key]) !== type$1[key]
                    && !IS(type$1[key])(value[key])) {
                    return false;
                }
            }
            return true;
        };
    }
    if (isArray(type$1)) {
        return (value) => {
            if (!isArray(value) || value.length !== type$1.length)
                return false;
            for (let i = 0; i < value.length; i++) {
                if (type(value[i]) !== type$1[i]
                    && !IS(type$1[i])(value[i])) {
                    return false;
                }
            }
            return true;
        };
    }
    return (value) => {
        return type$1 === type(value);
    };
}

export { EMPTY, FileTypeMap, INFINITY, IS, MAX_VALUES_NUMBER, MIN_VALUES_NUMBER, hasOwn, isArray, isArrayIterator, isAsyncFunction, isBigInt, isBoolean, isClient, isDate, isEffectArray, isEffectFunction, isEffectMap, isEffectNumber, isEffectObject, isEffectSet, isEmpty, isEmptyArray, isEmptyFunction, isEmptyMap, isEmptyObject, isEmptySet, isError, isExcelFile, isFile, isFileExtension, isFloat, isFunction, isHexColor, isIOS, isImageFile, isInstance, isInteger, isIterator, isJsonString, isMap, isMapIterator, isNaN$1 as isNaN, isNoEmpty, isNoNull, isNoUndefined, isNull, isNumber, isNumberArray, isObject, isPdfFile, isPromise, isRegExp, isSet, isSetIterator, isString, isSymbol, isTime, isUndefined, isValid, isVideoFile, isWindow, isWordFile, likeDate, likeFunction, likeNumber, type, types };
//# sourceMappingURL=index.esm.js.map
